// Shared trade-portal hooks and form controls: reference data, catalogue dropdown, country / port pickers,
// Incoterm chips and small display helpers. Data hooks cache aggressively — this is slow-changing reference data.
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Mail, MessageCircle, Phone } from 'lucide-react';
import { trade } from '../../lib/trade';
import { Badge } from '../../components/ui';
import type { Contact, OrgKind, OrgSummary, Product, Reference } from '../../types/trade';

export function useReference() {
  return useQuery<Reference>({ queryKey: ['trade-reference'], queryFn: ({ signal }) => trade.reference(signal), staleTime: Infinity });
}

/** Approved catalogue products (the dropdown source everywhere). */
export function useCatalog(status: string = 'approved') {
  return useQuery<Product[]>({ queryKey: ['catalog', status], queryFn: ({ signal }) => trade.catalog({ status }, signal), staleTime: 5 * 60_000 });
}

export function usePorts(country?: string) {
  return useQuery({ queryKey: ['ports', country ?? ''], queryFn: ({ signal }) => trade.ports({ country }, signal), staleTime: 10 * 60_000 });
}

export function countryName(ref: Reference | undefined, code: string): string {
  return ref?.countries.find((c) => c.code === code)?.name ?? code;
}

export function CountrySelect({ value, onChange, required, allowEmpty, emptyLabel = 'Any country', id, name }: {
  value: string; onChange: (code: string) => void; required?: boolean; allowEmpty?: boolean; emptyLabel?: string; id?: string; name?: string;
}) {
  const ref = useReference();
  const markets = ref.data?.lithium_markets ?? [];
  const all = [...(ref.data?.countries ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <select id={id} name={name} value={value} required={required} onChange={(e) => onChange(e.target.value)} disabled={!ref.data}>
      {(allowEmpty || !value) && <option value="">{ref.data ? emptyLabel : 'Loading countries…'}</option>}
      <optgroup label="Lithium trade markets">
        {markets.map((code) => <option key={`m-${code}`} value={code}>{countryName(ref.data, code)}</option>)}
      </optgroup>
      <optgroup label="All countries">
        {all.filter((c) => !markets.includes(c.code)).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
      </optgroup>
    </select>
  );
}

export const CATEGORY_LABEL: Record<string, string> = {
  raw_material: 'Mining & raw materials',
  battery_chemical: 'Chemicals & metals',
  battery_material: 'Cathode & anode materials',
  battery_component: 'Cell components',
  battery: 'Cells & packs',
  recycling: 'Recycling',
  battery_scrap: 'Battery scrap',
  ewaste: 'E-waste',
  nonferrous_scrap: 'Copper & aluminium scrap',
  base_metal: 'Base metals & busbars',
  critical_mineral: 'Other critical minerals',
  plant_input: 'Plant inputs (reagents & by-products)',
  other: 'Other',
};
export const TRADE_CATEGORY_ORDER = [
  'raw_material', 'battery_chemical', 'battery_material', 'battery_component', 'battery', 'recycling', 'battery_scrap', 'ewaste', 'nonferrous_scrap',
  'base_metal', 'critical_mineral', 'other',
];
export const categoryLabel = (c: string) => CATEGORY_LABEL[c] ?? c;

/** Money in a few words: US$9,000 or US$8.50. */
export function money(value: number | null | undefined): string {
  if (value == null) return '';
  return `US$${new Intl.NumberFormat('en-US', { maximumFractionDigits: value < 100 ? 2 : 0 }).format(value)}`;
}

/** Indian rupees: ₹290 or ₹2,49,900 (Indian digit grouping). */
export function rupees(value: number | null | undefined): string {
  if (value == null) return '';
  return `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: value < 100 ? 2 : 0 }).format(value)}`;
}

/** A listing's indicative price, in US$ and/or ₹, per its unit and Incoterm. */
export function listingPrice(l: { price_usd: number | null; price_inr?: number | null; quantity_unit: string; price_basis: string; price_note: string }): string {
  const parts = [l.price_usd != null && `${money(l.price_usd)}/${l.quantity_unit}`, l.price_inr != null && `${rupees(l.price_inr)}/${l.quantity_unit}`].filter(Boolean);
  return parts.length ? `${parts.join(' · ')}${l.price_basis ? ` ${l.price_basis}` : ''}` : (l.price_note || '—');
}

/** One line for an offer or a need: "Lithium hydroxide · 50–500 t / month · US$9,000/t FOB". */
export function offerLine(o: { kind: string; product: string; min: number | null; max: number | null; unit: string; price_usd: number | null; price_basis: string }): string {
  const fmt = (n: number) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(n);
  const qty = o.min != null && o.max != null && o.min !== o.max ? `${fmt(o.min)}–${fmt(o.max)} ${o.unit}` : o.max != null || o.min != null ? `${fmt((o.max ?? o.min) as number)} ${o.unit}` : '';
  const price = o.price_usd != null ? `${money(o.price_usd)}/${o.unit}${o.price_basis ? ` ${o.price_basis}` : ''}` : '';
  return [o.product.split(',')[0], qty, price].filter(Boolean).join(' · ');
}

export function ProductSelect({ value, onChange, required, allowEmpty, emptyLabel = 'All products', id }: {
  value: string; onChange: (id: string) => void; required?: boolean; allowEmpty?: boolean; emptyLabel?: string; id?: string;
}) {
  const catalog = useCatalog();
  const groups = new Map<string, Product[]>();
  for (const p of catalog.data ?? []) groups.set(p.category, [...(groups.get(p.category) ?? []), p]);
  return (
    <select id={id} value={value} required={required} onChange={(e) => onChange(e.target.value)} disabled={!catalog.data}>
      {(allowEmpty || !value) && <option value="">{catalog.data ? (allowEmpty ? emptyLabel : 'Choose a product…') : 'Loading catalogue…'}</option>}
      {[...groups.entries()].map(([cat, items]) => (
        <optgroup key={cat} label={categoryLabel(cat)}>
          {items.map((p) => <option key={p.id} value={p.id}>{p.name}{p.hs_code ? ` · HS ${p.hs_code}` : ''}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

export function PortSelect({ country, value, onChange, id }: { country: string; value: string; onChange: (id: string) => void; id?: string }) {
  const ports = usePorts(country || undefined);
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)} disabled={!ports.data}>
      <option value="">{ports.data ? (ports.data.length ? 'No port (address only)' : 'No ports listed for this country') : 'Loading ports…'}</option>
      {(ports.data ?? []).map((p) => (
        <option key={p.id} value={p.id}>{p.name}{p.locode ? ` (${p.locode})` : ''}{p.kind === 'icd' ? ' · inland depot' : ''}{p.region ? ` · ${p.region}` : ''}</option>
      ))}
    </select>
  );
}

export function IncotermPicker({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const ref = useReference();
  const terms = ref.data?.incoterms ?? ['FOB', 'CFR', 'CIF', 'DAP'];
  const toggle = (t: string) => onChange(value.includes(t) ? value.filter((x) => x !== t) : [...value, t]);
  return (
    <div className="chip-picker" role="group" aria-label="Incoterms">
      {terms.map((t) => (
        <button key={t} type="button" className="chip-toggle" aria-pressed={value.includes(t)} onClick={() => toggle(t)}>{t}</button>
      ))}
    </div>
  );
}

export function KindBadge({ kind }: { kind: OrgKind }) {
  return <Badge tone={kind === 'seller' ? 'amber' : kind === 'buyer' ? 'info' : 'neutral'}>{kind === 'both' ? 'Buyer & seller' : kind === 'seller' ? 'Seller' : 'Buyer'}</Badge>;
}

export function SourceBadge({ org }: { org: Pick<OrgSummary, 'source' | 'verification'> }) {
  if (org.verification === 'verified') return <Badge tone="good" title="Checked by staff">Verified</Badge>;
  if (org.source === 'registered') return <Badge tone="info" title="Registered on this platform; details self-declared">Registered</Badge>;
  if (org.source === 'discovered') return <Badge title="Profile built from the company's own website; contact the company directly">Web profile</Badge>;
  return <Badge title="Added by staff">Added by staff</Badge>;
}

export function ScoreBadge({ score, label }: { score: number; label?: string | null }) {
  const tone = score >= 0.65 ? 'good' : score >= 0.45 ? 'amber' : 'neutral';
  return <Badge tone={tone} title="Rule-based match score (product, role, contactability, country evidence)">{label ? `${label} · ` : ''}{score.toFixed(2)}</Badge>;
}

export function ContactLine({ c }: { c: Contact }) {
  return (
    <div className="contact-line">
      <div>
        <strong>{c.name || c.title || DESK_LABEL[c.department] || 'Company contact'}</strong>{c.name && c.title && <span className="muted"> · {c.title}</span>}
        {c.is_primary && <Badge tone="info">Primary</Badge>}
        {c.visibility === 'private' && <Badge>Private</Badge>}
        {c.suppressed && <Badge tone="bad">Do not contact</Badge>}
      </div>
      <div className="contact-channels">
        {c.email && <a href={`mailto:${c.email}`}><Mail size={13} aria-hidden />{c.email}</a>}
        {c.phone && <a href={`tel:${c.phone.replace(/[^+\d]/g, '')}`}><Phone size={13} aria-hidden />{c.phone}</a>}
        {c.whatsapp && <a href={`https://wa.me/${c.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"><MessageCircle size={13} aria-hidden />WhatsApp</a>}
      </div>
      {c.source === 'discovered' && c.source_url && (
        <div className="tiny muted">Published on <a href={c.source_url} target="_blank" rel="noopener noreferrer nofollow">{hostOf(c.source_url)}</a></div>
      )}
    </div>
  );
}

const DESK_LABEL: Record<string, string> = {
  general: 'Company contact', sales: 'Sales desk', procurement: 'Procurement desk', export: 'Export / international desk', logistics: 'Logistics desk',
};

/** One desk (or person) with every address and number published for it. */
export function ContactCard({ c }: { c: Contact }) {
  const heading = c.name || c.title || DESK_LABEL[c.department] || 'Company contact';
  const sub = c.name ? c.title : '';
  const emails = [c.email, ...(c.more_emails ?? [])].filter(Boolean);
  const phones = [c.phone, ...(c.more_phones ?? [])].filter(Boolean);
  return (
    <div className="contact-card">
      <div className="contact-card-head">
        <div className="contact-card-person">
          <div className="contact-avatar">{heading.charAt(0).toUpperCase()}</div>
          <div className="contact-card-meta">
            <strong>{heading}</strong>
            {sub && <div className="contact-title tiny muted">{sub}</div>}
          </div>
        </div>
        <div className="contact-card-badges">
          {c.is_primary && <Badge tone="info">Primary</Badge>}
          {c.visibility === 'private' && <Badge>Private</Badge>}
          {c.suppressed && <Badge tone="bad">Do not contact</Badge>}
        </div>
      </div>
      <div className="contact-card-actions">
        {emails.map((e) => (
          <a key={e} href={`mailto:${e}`} className="contact-pill email" title={`Email ${e}`}><Mail size={12} aria-hidden /><span>{e}</span></a>
        ))}
        {phones.map((p) => (
          <a key={p} href={`tel:${p.replace(/[^+\d]/g, '')}`} className="contact-pill phone" title={`Call ${p}`}><Phone size={12} aria-hidden /><span>{p}</span></a>
        ))}
        {c.whatsapp && (
          <a href={`https://wa.me/${c.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="contact-pill whatsapp" title="Chat on WhatsApp">
            <MessageCircle size={12} aria-hidden /><span>WhatsApp {c.whatsapp}</span>
          </a>
        )}
      </div>
      {c.source === 'discovered' && c.source_url && (
        <div className="tiny muted contact-source">As published on <a href={c.source_url} target="_blank" rel="noopener noreferrer nofollow">{hostOf(c.source_url)}</a></div>
      )}
    </div>
  );
}

export function ExtLink({ href, children }: { href: string; children?: ReactNode }) {
  return <a href={href} target="_blank" rel="noopener noreferrer nofollow" className="ext-link">{children ?? hostOf(href)}<ExternalLink size={12} aria-hidden /></a>;
}

export function hostOf(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
}

export function qty(n: number | null | undefined, unit: string): string {
  if (n == null) return '—';
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(n)} ${unit}`;
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Turn a comma/newline separated text field into a clean list. */
export function splitList(text: string): string[] {
  return text.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
}
